import QueueModalWrapper from "./QueueModalWrapper";
import {Button} from "@varymadellc/my-weed-club-component-library";
import React, {useEffect, useRef, useState} from "react";
import {useAuth} from "../../../providers/AuthProvider";
import {apiPost, extractPayloadFromResponse} from "../../../services/apiService";
import {useSiteMessage} from "../../../providers/SiteMessageProvider";
import Input from "../../../components/Input/Input";
import ShareGiphyBookmark from "@/components/ShareGiphyBookmark/ShareGiphyBookmark";

export interface QueueModalGiphyProps {
    setIsOpen: (value: string) => void;
    requestRefresh?: () => void;
}

export default function QueueModalGiphy({setIsOpen, requestRefresh}: QueueModalGiphyProps) {
    const [giphy, setGiphy] = useState<string>('');

    const {authToken} = useAuth();

    const {setMessage} = useSiteMessage();

    const [embed, setEmbed] = useState<null | {
        embed: string;
        referringUrl: string;
        title?: string;
    }>(null);

    const setIsOpenHandler = (value: string) => {
        if (!value) {
            setEmbed(null);

            setIsOpen(value || '');
        }
    }

    function getSrcFromEmbed(embed: string) {
        return embed.match(/src="([^"]+)"/)?.[1] || '';
    }

    function getTitleFromEmbed(embed: string) {
        return embed.match(/title="(.*)"/)?.[1] || '';
    }

    async function postData(data) {
        try {
            if (!data?.embed) {
                throw new Error('Embed is required');
            }

            if (!/<iframe[^>]*src=["'](https?:\/\/giphy\.com\/embed\/[^?]+)(?:\?[^"']*)?["'][^>]*>/.test(data.embed)) {
                throw new Error('Embed is invalid');
            }

            const src = getSrcFromEmbed(data.embed);

            if (!src || !src.includes('giphy')) {
                throw new Error('Embed is an invalid giphy embed');
            }

            const referringUrl = window.location.href;

            const response = await apiPost('/q', JSON.stringify({
                type: 'giphy',
                metadata: {
                    src,
                    title: data?.title || '',
                    referringUrl
                }
            }), authToken);

            const {data: queueItem} = await extractPayloadFromResponse(response);

            if (queueItem) {
                setMessage('Giphy video added to Queue');

                setIsOpen('');

                setEmbed(null);

                requestRefresh && requestRefresh();
            }
        } catch (error: any) {
            setMessage(error.message);
        }
    }

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        const giphySearchParam = urlParams.get('giphy');

        if (giphySearchParam) {
            setGiphy(giphySearchParam);
        }
    }, [window.location.search]);

    useEffect(() => {
        if (giphy) {
            const giphyDecoded = decodeURIComponent(giphy);

            setEmbed(() => JSON.parse(giphyDecoded));

            const timeout = setTimeout(() => {
                onSubmitHandler({preventDefault: () => null}, giphyDecoded);

                const url = new URL(window.location.href);

                url.searchParams.delete('giphy');

                window.history.pushState(null, '', url.toString());
            }, 1000);

            return () => clearTimeout(timeout);
        }
    }, [giphy]);

    const onSubmitHandler = (event: any, context?: any) => {
        event.preventDefault();

        postData(embed || context);
    };

    const embedInputHandler = (event: any) => {
        const embedData = {
            embed: event.target.value,
            referringUrl: window.location.href,
            title: 'Giphy Video'
        };

        setEmbed(embedData);
    };

    return (
        <QueueModalWrapper setIsOpen={setIsOpenHandler}>
            <div className="relative flex flex-col p-2">
                <h2>Giphy Video via embed</h2>
                <hr className="mt-2"/>
                <form onSubmit={onSubmitHandler} className="flex flex-col gap-4">
                    {
                       giphy
                            ?
                            (
                                <div className="flex flex-col gap-4 justify-center items-center p-4">
                                    <p>We are submitting your Giphy link now. Thank you, the q should refresh
                                        shortly.</p>
                                </div>
                            )
                            :
                            (
                                <>
                                    <label htmlFor="giphy">Giphy Embed Code</label>
                                    <Input type="text" value={embed?.embed || ''} name="giphy"
                                           onChange={embedInputHandler}/>
                                    <Button type="submit">Add</Button>
                                    <p>Just go over to <a href="https://giphy.com">Giphy.com</a>, find an image or gif you like,
                                        and grab an embed code or make it easy and use one of the quick add bookmarklets.</p>
                                    <ShareGiphyBookmark/>
                                </>
                            )
                    }
                </form>
            </div>
        </QueueModalWrapper>
    );
}